import { useNavigate } from "react-router-dom";
import { Sparkles, ShieldCheck, Zap, X } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * PremiumModal — soft upsell shown when a free-tier user tries to do something
 * gated behind a paid plan (currently: defeating a 3rd bill).
 *
 * Props:
 *  - open: bool
 *  - onOpenChange(boolean)
 *  - title?: string (default "Upgrade to Guardian")
 *  - message?: string
 *  - cta?: string (default "Upgrade Now")
 */
export default function PremiumModal({ open, onOpenChange, title, message, cta }) {
  const navigate = useNavigate();

  const goUpgrade = () => {
    onOpenChange?.(false);
    navigate("/upgrade");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md rounded-3xl border-2 border-amber-300 p-0 overflow-hidden"
        data-testid="premium-modal"
      >
        {/* Hero band — gold gradient with sparkle bg */}
        <div
          className="relative px-6 pt-7 pb-6 text-slate-900"
          style={{ background: "linear-gradient(135deg, #FDE68A 0%, #F59E0B 60%, #B45309 110%)" }}
        >
          <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full pointer-events-none"
               style={{ background: "radial-gradient(circle, rgba(255,255,255,0.55), rgba(255,255,255,0) 60%)" }} />
          <button
            type="button"
            onClick={() => onOpenChange?.(false)}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/40 hover:bg-white/70 flex items-center justify-center text-slate-900 transition"
            aria-label="Close"
            data-testid="premium-modal-close"
          >
            <X className="w-4 h-4" strokeWidth={3} />
          </button>
          <div className="relative flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-slate-900 text-amber-400 flex items-center justify-center ring-4 ring-white/40 shadow">
              <ShieldCheck className="w-6 h-6" strokeWidth={2.6} />
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-900">Power up</p>
              <h2
                className="text-2xl font-black leading-tight"
                style={{ fontFamily: "Outfit, sans-serif" }}
                data-testid="premium-modal-title"
              >
                {title || "Upgrade to Guardian"}
              </h2>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-6 bg-white">
          <p className="text-base font-semibold text-slate-700 leading-relaxed" data-testid="premium-modal-message">
            {message || "Upgrade to Guardian to defeat more bills and keep raising your Hero Score!"}
          </p>

          <ul className="mt-4 space-y-2">
            {[
              "Unlimited bill defeats — no caps",
              "Auto-sync subscriptions with Plaid",
              "Hero Alerts when prices hike",
            ].map((perk) => (
              <li key={perk} className="flex items-start gap-2 text-sm text-slate-700">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-3 h-3" strokeWidth={3} />
                </span>
                <span className="font-medium">{perk}</span>
              </li>
            ))}
          </ul>

          <div className="mt-6 flex flex-col gap-2">
            <Button
              onClick={goUpgrade}
              className="w-full h-12 rounded-2xl text-slate-900 font-black tracking-wide shadow-lg shadow-amber-400/40 hover:shadow-xl hover:scale-[1.01] active:scale-[0.99] transition-all"
              style={{ background: "linear-gradient(135deg,#FBBF24,#F59E0B)", fontFamily: "Outfit, sans-serif" }}
              data-testid="premium-modal-upgrade-button"
            >
              <Zap className="w-4 h-4 mr-2" strokeWidth={3} />
              {cta || "Upgrade Now"}
            </Button>
            <Button
              variant="ghost"
              onClick={() => onOpenChange?.(false)}
              className="w-full h-10 rounded-2xl text-slate-500 font-bold tracking-wide hover:text-slate-900"
              data-testid="premium-modal-dismiss-button"
            >
              Maybe later
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
